import { computed, ref, watchEffect } from "vue";
import { createGlobalState } from "@vueuse/core";
import config from "@/config";
import useAuthState from "@/composables/state/authState";
import Screens from "@/screens";
import useApplicationsState from "@/composables/state/applicationsState";
import { TabType } from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";
import type { Component } from "vue";
import type {
  BorrowerPlatformTemplate,
  Tab,
} from "@/api/http/schemas/borrowerPlatformTemplatesSchemas";

export enum PlatformScreen {
  ApplicationEmpty = "ApplicationEmptyScreen",
  Application = "ApplicationScreen",
  CustomTab = "CustomTab",
  Dead = "DeadStageScreen",
  Documents = "DocumentsScreen",
  Faq = "FaqScreen",
  Profile = "ProfileScreen",
  Welcome = "WelcomeScreen",
}

const tabTypeToScreen: Record<Tab["type"], PlatformScreen | null> = {
  welcome_page: PlatformScreen.Welcome,
  custom: PlatformScreen.CustomTab,
  documents: PlatformScreen.Documents,
  faq: PlatformScreen.Faq,
  profile: PlatformScreen.Profile,
  workflow: PlatformScreen.Application,
  log_out: null,
};

const screenToComponent: Record<PlatformScreen, Component> = {
  [PlatformScreen.ApplicationEmpty]: Screens.ApplicationEmptyScreen,
  [PlatformScreen.Application]: Screens.ApplicationScreen,
  [PlatformScreen.CustomTab]: Screens.CustomTabScreen,
  [PlatformScreen.Dead]: Screens.DeadStageScreen,
  [PlatformScreen.Documents]: Screens.DocumentsScreen,
  [PlatformScreen.Faq]: Screens.FaqScreen,
  [PlatformScreen.Profile]: Screens.ProfileScreen,
  [PlatformScreen.Welcome]: Screens.WelcomeScreen,
};

const NO_HEADER_SCREENS: PlatformScreen[] = [
  PlatformScreen.Faq,
  PlatformScreen.Documents,
  PlatformScreen.Profile,
  PlatformScreen.CustomTab,
];

/**
 * @description Tab to select if the app is loaded with a `screen` query param
 * For example, `?screen=documents` should cause the app to select the `documents`
 * tab type on mount.
 */
const screenQueryToTab: Record<string, Tab["type"]> = {
  documents: TabType.documents,
};

const usePlatformScreenState = createGlobalState(() => {
  const { isLoggedIn } = useAuthState();
  const { allApplications, isDeclined } = useApplicationsState();

  const getSelectedTabIdFromUrl = (): Tab["id"] | null => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get("tab") || null;
  };

  const setSelectedTabIdInUrl = (tabId: Tab["id"] | null) => {
    const url = new URL(window.location.href);
    if (tabId) {
      url.searchParams.set("tab", tabId);
    } else {
      url.searchParams.delete("tab");
    }
    window.history.replaceState({}, "", url.toString());
  };

  const selectedTabId = ref<Tab["id"] | null>(getSelectedTabIdFromUrl());

  const activePlatformTemplate = ref<BorrowerPlatformTemplate>();
  const showLoadingFullscreen = ref(false);

  const visibleTabs = computed(() => {
    return (
      activePlatformTemplate.value?.content.tabs.filter((tab) => {
        if (
          tab.type === TabType.workflow &&
          tab.workflow_template_ids?.length &&
          !allApplications.value.some((application) =>
            tab.workflow_template_ids?.includes(application.workflow_template_id)
          )
        ) {
          return false;
        }

        if (tab.visibility === "always") {
          return true;
        }
        if (tab.visibility === "authenticated") {
          return isLoggedIn.value;
        }
        if (tab.visibility === "unauthenticated") {
          return !isLoggedIn.value;
        }

        return false;
      }) ?? []
    );
  });

  const selectedTab = computed<Tab | null>(
    () => visibleTabs.value.find((tab) => tab.id === selectedTabId.value) ?? null
  );

  const selectedTabScreen = computed(() => {
    if (!selectedTab.value) {
      return isLoggedIn.value ? PlatformScreen.Application : PlatformScreen.Welcome;
    }

    return tabTypeToScreen[selectedTab.value.type];
  });

  const welcomeScreenTab = computed(() => {
    // As of writing, the dashboard only allows creating one welcome tab per template,
    // so grabbing the first one here should be safe.
    return activePlatformTemplate.value?.content.tabs.find(
      (tab) => tab.type === TabType.welcomePage
    );
  });

  const currentScreen = computed(() => {
    if (!isLoggedIn.value) {
      return selectedTabScreen.value ?? PlatformScreen.Welcome;
    }

    if (!allApplications.value.length) {
      return PlatformScreen.ApplicationEmpty;
    }

    if (selectedTabScreen.value === PlatformScreen.Application && isDeclined.value) {
      return PlatformScreen.Dead;
    }

    //TODO: Add a default error screen instead of defaulting to auth
    return selectedTabScreen.value ?? PlatformScreen.Welcome;
  });

  const currentScreenComponent = computed(() => screenToComponent[currentScreen.value]);

  const screenHasHeader = computed(() => {
    if (currentScreen.value !== PlatformScreen.Welcome) {
      return !NO_HEADER_SCREENS.includes(currentScreen.value);
    }

    return selectedTab.value?.show_header ?? true;
  });

  const setSelectedTabById = (id: Tab["id"]) => {
    selectedTabId.value = id;
    setSelectedTabIdInUrl(id);
  };

  /**
   * Will select the first tab of the given type if the template contains
   * multiple tabs with the same type.
   */
  const setSelectedTabByType = (type: Tab["type"]) => {
    const tabToSelect = visibleTabs.value.find((tab) => tab.type === type);

    if (!tabToSelect) {
      return;
    }

    setSelectedTabById(tabToSelect.id);
  };

  watchEffect(() => {
    const template = activePlatformTemplate.value;
    const tabs = template?.content?.tabs;
    const loggedIn = isLoggedIn.value;
    const currentSelectedTabId = selectedTabId.value;
    const currentVisibleTabs = visibleTabs.value;

    if (!tabs?.length) {
      return;
    }

    if (!loggedIn) {
      const welcomeTab = tabs.find((tab) => tab.type === TabType.welcomePage);

      if (welcomeTab) {
        setSelectedTabById(welcomeTab.id);
      }

      return;
    }

    const currentSelectedTabIsValid =
      !!currentSelectedTabId && currentVisibleTabs.some((tab) => tab.id === currentSelectedTabId);

    if (currentSelectedTabIsValid) {
      return;
    }

    if (!!config.page && screenQueryToTab[config.page]) {
      setSelectedTabByType(screenQueryToTab[config.page]);
      return;
    }

    const firstTab = currentVisibleTabs[0];

    if (firstTab) {
      setSelectedTabById(firstTab.id);
      return;
    }
  });

  return {
    activePlatformTemplate,
    currentScreen,
    currentScreenComponent,
    screenHasHeader,
    selectedTab,
    selectedTabId,
    showLoadingFullscreen,
    visibleTabs,
    welcomeScreenTab,
    setSelectedTabById,
    setSelectedTabByType,
  };
});

export default usePlatformScreenState;
